import { Breakpoint } from '@mui/material';

/**
 * Returns true if the browser supports `matchMedia`. Otherwise, returns false.
 *
 * All of the real browsers we support have supported `matchMedia` for many years.
 *  https://caniuse.com/matchmedia
 *
 * Unfortunately, jsdom, the browser used by jest, does not support `matchMedia`. We use this to enable this code to
 * gently fallback (instead of catastrophically fail) in test environments, so we do not have to fight with mocking
 *  `matchMedia` everywhere.
 */
export function getSupportsMatchMedia(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined';
}

// Fallback breakpoint that will be used if `matchMedia` is unavailable. Set to `xl` because this is the most common
// breakpoint seen in our customer usage in fullstory, so in the very rare case this is hit in a production use case,
// we use the breakpoint that makes the most sense for most users.
export const FALLBACK_BREAKPOINT: Breakpoint = 'xl';

/**
 * Returns a very simplistic fallback implementation of `MediaQueryList` to use as a simple
 * fallback in cases where `matchMedia` is not defined (primarily testing in jsdom).
 *
 * It takes a single optional argument that determines if `matches` will be true or false, with a
 * default of false.
 *
 * All methods behave as noops. This is not intended as a fully featured polyfill for `matchMedia`. It is just a
 * simple shim to keep the app from breaking if `matchMedia` is missing. This should be incredibly rare in
 * production use cases because all browsers we support have supported `matchMedia` for many years.
 */
export const getFallbackMatchMedia = (matches: boolean): MediaQueryList => {
  return {
    matches: matches,
    media: '',
    onchange: () => {},
    addListener: () => {},
    removeListener: () => {},
    addEventListener: () => {},
    removeEventListener: () => {},
    dispatchEvent: () => true,
  };
};
